import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function debugAuth(label, payload) {
  if (process.env.REACT_APP_RUNTIME_PHASE !== "testing") {
    return;
  }

  console.log(`[auth] ${label}`, payload);
}

function isUuid(value) {
  return typeof value === "string" && UUID_V4_PATTERN.test(value);
}

export default function useAuth() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [activeTenant, setActiveTenant] = useState(null);
  const [userTenants, setUserTenants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileResolutionUserId, setProfileResolutionUserId] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchSessionAndListen = async () => {
      try {
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          throw sessionError;
        }

        let resolvedUser = null;
        const session = data.session ?? null;

        debugAuth("bootstrap.session", {
          hasSession: Boolean(session),
          sessionUserId: session?.user?.id ?? null,
          sessionEmail: session?.user?.email ?? null,
        });

        if (session?.access_token) {
          const { data: userData, error: userError } = await supabase.auth.getUser(session.access_token);

          if (userError) {
            console.warn("Discarding invalid cached auth session:", userError.message);
            debugAuth("bootstrap.invalid-session", {
              sessionUserId: session?.user?.id ?? null,
              error: userError.message,
            });
            await supabase.auth.signOut({ scope: 'local' });
          } else {
            resolvedUser = userData?.user ?? null;
            debugAuth("bootstrap.verified-user", {
              userId: resolvedUser?.id ?? null,
              email: resolvedUser?.email ?? null,
            });
          }
        }

        if (!isMounted) return;

        setUser(resolvedUser);

        const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
          debugAuth("auth-state-change", {
            eventUserId: session?.user?.id ?? null,
            eventEmail: session?.user?.email ?? null,
            hasSession: Boolean(session),
          });
          setUser(session?.user ?? null);
        });

        setIsLoading(false);

        return () => {
          listener?.subscription.unsubscribe();
        };
      } catch (error) {
        if (!isMounted) return;
        if (error?.name === "AbortError" || String(error?.message || "").includes("signal is aborted without reason")) {
          return;
        }

        console.error("Failed to load auth session:", error);
        setIsLoading(false);
      }
    };

    const cleanupPromise = fetchSessionAndListen();

    return () => {
      isMounted = false;
      cleanupPromise?.then((cleanup) => cleanup?.());
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      setActiveTenant(null);
      setUserTenants([]);
      setIsProfileLoading(false);
      setProfileResolutionUserId(null);
      return;
    }

    const fetchUserProfileAndTenants = async () => {
      setProfileResolutionUserId(null);
      setIsProfileLoading(true);

      try {
        // Fetch user profile data
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select(
            "id, first_name, last_name, display_name, is_admin, can_upload, can_create_project, last_seen_revision_check, tenant_id, created_at"
          )
          .eq("id", user.id)
          .maybeSingle();

        debugAuth("profile.lookup", {
          userId: user.id,
          email: user.email ?? null,
          hasProfile: Boolean(profileData),
          profileId: profileData?.id ?? null,
          profileError: profileError ? {
            message: profileError.message,
            code: profileError.code ?? null,
            details: profileError.details ?? null,
          } : null,
        });

        if (profileError) {
          // console.error("Error fetching profile:", profileError.message);
          setUserProfile(null);
          return;
        }

        // Fetch user's tenant memberships
        const { data: tenantData, error: tenantError } = await supabase
          .from("tenant_memberships")
          .select(`
            tenant_id,
            role,
            tenants (
              id,
              name,
              slug,
              status
            )
          `)
          .eq("user_id", user.id);

        if (tenantError && tenantError.name !== "AbortError") {
          console.error("Error fetching tenant memberships:", tenantError.message);
        }

        const tenants = tenantData
          ? tenantData
              .map((tm) => ({
                id: tm?.tenants?.id,
                name: tm?.tenants?.name,
                slug: tm?.tenants?.slug,
                status: tm?.tenants?.status,
                role: tm?.role,
              }))
              .filter((tenant) => isUuid(tenant.id))
          : [];

        debugAuth("tenant-memberships.lookup", {
          userId: user.id,
          tenantCount: tenants.length,
          tenantError: tenantError ? {
            message: tenantError.message,
            code: tenantError.code ?? null,
            details: tenantError.details ?? null,
          } : null,
        });

        // Set active tenant from localStorage or default to first tenant
        const storedTenantId = localStorage.getItem('activeTenantId');
        // Validate that stored tenant ID is a valid UUID before using it
        const isValidUuid = isUuid(storedTenantId);
        const defaultTenant = tenants.find(t => t.slug === 'default') || tenants[0];
        const initialActiveTenant = (isValidUuid && storedTenantId)
          ? tenants.find(t => t.id === storedTenantId) || defaultTenant
          : defaultTenant;

        setUserTenants(tenants);
        setActiveTenant(initialActiveTenant);

        // Fetch accessible project IDs from project_access table
        const { data: accessData, error: accessError } = await supabase
          .from("project_access")
          .select("project_id")
          .eq("user_id", user.id);

        if (accessError && accessError.name !== "AbortError") {
          // console.error("Error fetching project access:", accessError.message);
        }

        const accessible_project_ids = accessData ? accessData.map((p) => p.project_id) : [];

        debugAuth("profile.ready", {
          userId: user.id,
          profileId: profileData?.id ?? null,
          tenantCount: tenants.length,
          accessibleProjectCount: accessible_project_ids.length,
          onboardingCompleted: Boolean(user.user_metadata?.onboarding_completed),
        });

        setUserProfile({
          ...profileData,
          accessible_project_ids,
          can_upload:
            profileData.can_upload === true ||
            profileData.can_upload === "true" ||
            profileData.can_upload === 1,
          can_create_project:
            profileData.can_create_project === true ||
            profileData.can_create_project === "true" ||
            profileData.can_create_project === 1,
        });
      } catch (error) {
        if (error?.name === "AbortError" || String(error?.message || "").includes("signal is aborted without reason")) {
          return;
        }

        console.error("Failed to load user profile:", error);
        setUserProfile(null);
      } finally {
        setIsProfileLoading(false);
        setProfileResolutionUserId(user.id);
      }
    };

    fetchUserProfileAndTenants();
  }, [user]);

  const authFlowState = !user
    ? 'signed-out'
    : isProfileLoading || profileResolutionUserId !== user.id
      ? 'resolving-profile'
      : userProfile
        ? 'ready'
        : user?.user_metadata?.onboarding_completed
          ? 'pending-assignment'
          : 'onboarding';

  useEffect(() => {
    debugAuth("flow-state", {
      userId: user?.id ?? null,
      email: user?.email ?? null,
      hasProfile: Boolean(userProfile),
      profileId: userProfile?.id ?? null,
      isLoading,
      isProfileLoading,
      onboardingCompleted: Boolean(user?.user_metadata?.onboarding_completed),
      authFlowState,
    });
  }, [authFlowState, isLoading, isProfileLoading, user, userProfile]);

  // Update Supabase client headers when active tenant changes
  useEffect(() => {
    if (activeTenant && isUuid(activeTenant.id)) {
      // Set tenant context in Supabase session
      supabase.rpc('set_tenant_context', { p_tenant_id: activeTenant.id }).then(
        () => debugAuth('tenant-context.set-success', { tenantId: activeTenant.id }),
        (err) => console.error('Failed to set tenant context:', err)
      );
      
      // Store active tenant in localStorage
      localStorage.setItem('activeTenantId', activeTenant.id);
      window.dispatchEvent(new CustomEvent('active-tenant-changed', {
        detail: { tenantId: activeTenant.id },
      }));
    } else if (activeTenant) {
      console.warn('Ignoring invalid active tenant id value');
      localStorage.removeItem('activeTenantId');
      window.dispatchEvent(new CustomEvent('active-tenant-changed', {
        detail: { tenantId: null },
      }));
    }
  }, [activeTenant]);

  const handleSetActiveTenant = (tenant) => {
    if (tenant && !isUuid(tenant.id)) {
      console.warn('Ignoring tenant switch with invalid tenant id value');
      return;
    }

    setActiveTenant(tenant);
    if (tenant && isUuid(tenant.id)) {
      localStorage.setItem('activeTenantId', tenant.id);
      window.dispatchEvent(new CustomEvent('active-tenant-changed', {
        detail: { tenantId: tenant.id },
      }));
    } else {
      localStorage.removeItem('activeTenantId');
      window.dispatchEvent(new CustomEvent('active-tenant-changed', {
        detail: { tenantId: null },
      }));
    }
  };

  return { 
    user, 
    userProfile, 
    activeTenant, 
    userTenants, 
    setActiveTenant: handleSetActiveTenant, 
    isLoading, 
    isProfileLoading,
    authFlowState,
    setUser, 
    setUserProfile 
  };
}
